import React, { useEffect, useRef, useState } from 'react';
import { Box, Slider, Typography, Button } from '@mui/material';
import { RestartAlt } from '@mui/icons-material';

const FRAME = 280;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Square crop matching the user event card and the ticket PDF.
 * zoom 1 is the smallest scale that still fills the square.
 * onChange receives { x, y, size } in the source image's natural pixels.
 */
const EventPosterCropper = ({ src, initialCrop, onChange }) => {
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  const emit = (nextZoom, nextOffset, dims) => {
    const size = dims || natural;
    if (!size || !onChange) return;
    const base = Math.max(FRAME / size.w, FRAME / size.h);
    const scale = base * nextZoom;
    const displayedW = size.w * scale;
    const displayedH = size.h * scale;
    const imgLeft = (FRAME - displayedW) / 2 + nextOffset.x;
    const imgTop = (FRAME - displayedH) / 2 + nextOffset.y;
    onChange({
      x: Math.max(0, -imgLeft / scale),
      y: Math.max(0, -imgTop / scale),
      size: FRAME / scale,
    });
  };

  const clampOffset = (nextZoom, nextOffset, dims) => {
    const size = dims || natural;
    if (!size) return nextOffset;
    const base = Math.max(FRAME / size.w, FRAME / size.h);
    const scale = base * nextZoom;
    const displayedW = size.w * scale;
    const displayedH = size.h * scale;
    const maxX = Math.max(0, (displayedW - FRAME) / 2);
    const maxY = Math.max(0, (displayedH - FRAME) / 2);
    return {
      x: clamp(nextOffset.x, -maxX, maxX),
      y: clamp(nextOffset.y, -maxY, maxY),
    };
  };

  useEffect(() => {
    setReady(false);
    setNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [src]);

  const handleLoad = (e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    const dims = { w, h };
    setNatural(dims);

    let nextZoom = 1;
    let nextOffset = { x: 0, y: 0 };

    if (initialCrop?.size > 0) {
      const base = Math.max(FRAME / w, FRAME / h);
      const scale = FRAME / initialCrop.size;
      nextZoom = clamp(scale / base, 1, 3);
      const appliedScale = base * nextZoom;
      const displayedW = w * appliedScale;
      const displayedH = h * appliedScale;
      nextOffset = {
        x: -initialCrop.x * appliedScale - (FRAME - displayedW) / 2,
        y: -initialCrop.y * appliedScale - (FRAME - displayedH) / 2,
      };
    }

    nextOffset = clampOffset(nextZoom, nextOffset, dims);
    setZoom(nextZoom);
    setOffset(nextOffset);
    setReady(true);
    emit(nextZoom, nextOffset, dims);
  };

  const applyZoom = (value) => {
    const nextZoom = clamp(value, 1, 3);
    const nextOffset = clampOffset(nextZoom, offset);
    setZoom(nextZoom);
    setOffset(nextOffset);
    emit(nextZoom, nextOffset);
  };

  const onPointerDown = (e) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const nextOffset = clampOffset(zoom, {
      x: dragRef.current.ox + (e.clientX - dragRef.current.x),
      y: dragRef.current.oy + (e.clientY - dragRef.current.y),
    });
    setOffset(nextOffset);
    emit(zoom, nextOffset);
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    emit(1, { x: 0, y: 0 });
  };

  const base = natural ? Math.max(FRAME / natural.w, FRAME / natural.h) : 1;
  const scale = base * zoom;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          width: FRAME,
          height: FRAME,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: '#111',
          border: '1px solid #444',
          cursor: natural ? 'grab' : 'default',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <Box
          component="img"
          ref={imgRef}
          src={src}
          alt="Poster crop"
          draggable={false}
          onLoad={handleLoad}
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: natural ? `${natural.w}px` : '100%',
            height: natural ? `${natural.h}px` : '100%',
            maxWidth: 'none',
            transform: natural
              ? `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`
              : 'none',
            transformOrigin: 'center center',
            opacity: ready ? 1 : 0,
            pointerEvents: 'none',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, px: 1 }}>
        <Typography variant="caption" sx={{ color: '#999', minWidth: 36 }}>Zoom</Typography>
        <Slider
          size="small"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(_, value) => applyZoom(value)}
          sx={{ color: '#667eea' }}
        />
        <Button
          size="small"
          startIcon={<RestartAlt />}
          onClick={reset}
          sx={{ color: '#bbb', textTransform: 'none', flexShrink: 0 }}
        >
          Reset
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
        Drag to move and use the slider to zoom. This square is what guests see and what is printed on the ticket PDF.
      </Typography>
    </Box>
  );
};

export default EventPosterCropper;
